import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { useTranslation } from 'react-i18next';
import { Autocomplete, IconButton, TextField, Typography } from '@totalsoft/rocket-ui';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { emptyString } from 'utils/constants';
import { StyledTableRow, StyledTableBodyCell } from 'components/common/Styles';
import { insuranceConventionDeductibleCostsValidator } from 'features/insuranceConventions/edit/validators';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import FlexBox from 'components/common/FlexBox';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const InsuranceConventionDeductibleCostContainer = ({
  deductibleCost,
  currencies,
  damageTypes,
  onAddOrUpdate,
  onDelete,
  onUpdateLocalDeductibleCost,
  onCancelAdd
}) => {
  const { cvsChPercent, cvsChAmount } = useFieldDecimalPlacesConfig();
  const [validation, validate] = useDirtyFieldValidation(insuranceConventionDeductibleCostsValidator);
  const editing = useMemo(() => deductibleCost.editing || deductibleCost.isNew, [deductibleCost.editing, deductibleCost.isNew]);
  const disabled = useMemo(() => deductibleCost.disabled, [deductibleCost.disabled]);
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const updateLocalDeductibleCost = useCallback(
    (property, value) => {
      onUpdateLocalDeductibleCost(deductibleCost.deductibleCostId, property, value);
    },
    [deductibleCost.deductibleCostId, onUpdateLocalDeductibleCost]
  );

  const handleEdit = useCallback(() => {
    updateLocalDeductibleCost('editing', true);
  }, [updateLocalDeductibleCost]);

  const handleCancelEdit = useCallback(() => {
    if (deductibleCost.isNew) onCancelAdd();
    else updateLocalDeductibleCost('editing', false);
  }, [deductibleCost.isNew, onCancelAdd, updateLocalDeductibleCost]);

  const handleAddOrUpdate = useCallback(
    input => {
      if (!validate(input)) return;

      onAddOrUpdate(input);
    },
    [onAddOrUpdate, validate]
  );

  const handleDelete = useCallback(() => {
    onDelete(deductibleCost.deductibleCostId);
  }, [deductibleCost.deductibleCostId, onDelete]);

  return (
    <StyledTableRow>
      {editing ? (
        <DeductibleCostEditView
          deductibleCost={deductibleCost}
          validation={validation}
          currencies={currencies}
          damageTypes={damageTypes}
          disabled={disabled || !canWrite}
          formatPercent={cvsChPercent}
          formatAmount={cvsChAmount}
          onAddOrUpdate={handleAddOrUpdate}
          onDelete={handleDelete}
          onCancelEdit={handleCancelEdit}
        />
      ) : (
        <DeductibleCostDetailsView
          deductibleCost={deductibleCost}
          disabled={disabled}
          formatPercent={cvsChPercent}
          formatAmount={cvsChAmount}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </StyledTableRow>
  );
};

const DeductibleCostDetailsView = ({ deductibleCost, disabled, formatPercent, formatAmount, onEdit, onDelete }) => {
  const { t } = useTranslation();

  return (
    <>
      <StyledTableBodyCell>
        <Typography>{deductibleCost.damageTypeName}</Typography>
      </StyledTableBodyCell>
      <StyledTableBodyCell>{deductibleCost.deductibleCostPercent?.toFixed(formatPercent)}</StyledTableBodyCell>
      <StyledTableBodyCell>{deductibleCost.deductibleCostAmount?.toFixed(formatAmount)}</StyledTableBodyCell>
      <StyledTableBodyCell>{deductibleCost.currencyCode}</StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton type='edit' color='primary' size='tiny' variant='text' onClick={onEdit} tooltip={t('Edit')} disabled={disabled} />
          <IconButton
            type='delete'
            color='primary'
            size='tiny'
            variant='text'
            onClick={onDelete}
            tooltip={t('Delete')}
            disabled={disabled}
          />
        </FlexBox>
      </StyledTableBodyCell>
    </>
  );
};

DeductibleCostDetailsView.propTypes = {
  deductibleCost: PropTypes.object,
  disabled: PropTypes.bool,
  formatPercent: PropTypes.number,
  formatAmount: PropTypes.number,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
};

const DeductibleCostEditView = ({
  deductibleCost,
  validation,
  currencies,
  damageTypes,
  disabled,
  formatPercent,
  formatAmount,
  onAddOrUpdate,
  onDelete,
  onCancelEdit
}) => {
  const { t } = useTranslation();

  const getLensValue = useCallback(
    () => ({
      ...deductibleCost
    }),
    [deductibleCost]
  );

  const [lens, dirtyInfo] = useChangeTrackingLens(getLensValue());
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  const handleAddOrUpdate = () => {
    onAddOrUpdate(lens |> get);
  };

  const handleChange = useCallback(
    (field, event) => {
      if (field === 'deductibleCostPercent' && event != null) {
        set(lens.deductibleCostAmount)(null);
        set(lens.deductibleCostPercent)(event);
      } else if (field === 'deductibleCostAmount' && event != null) {
        set(lens.deductibleCostPercent)(null);
        set(lens.deductibleCostAmount)(event);
      }
    },
    [lens]
  );

  const currencyChange = useCallback(
    value => {
      set(lens.currencyId)(value.id);
      set(lens.currencyCode)(value.code);
    },
    [lens.currencyId, lens.currencyCode]
  );

  const damageTypeChange = useCallback(
    value => {
      set(lens.damageTypeId)(value.id);
      set(lens.damageTypeName)(value.name);
    },
    [lens.damageTypeId, lens.damageTypeName]
  );

  return (
    <>
      <StyledTableBodyCell>
        {
          <Autocomplete
            label={t('InsuranceConventions.Details.DeductibleCosts.DamageType')}
            required
            value={{ id: lens.damageTypeId |> get, name: lens.damageTypeName |> get }}
            onChange={damageTypeChange}
            options={damageTypes}
            labelKey='name'
            valueKey='id'
            error={!isValid(validation.damageTypeId)}
            helperText={getErrors(validation.damageTypeId)}
            disabled={disabled}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        {
          <TextField
            label={t('InsuranceConventions.Details.DeductibleCosts.Percent')}
            isNumeric
            fullWidth
            decimalScale={formatPercent}
            value={(lens.deductibleCostPercent |> get) || emptyString}
            onChange={event => handleChange('deductibleCostPercent', event)}
            error={!isValid(validation.deductibleCostPercent)}
            helperText={getErrors(validation.deductibleCostPercent)}
            disabled={disabled}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        {
          <TextField
            label={t('InsuranceConventions.Details.DeductibleCosts.Value')}
            isNumeric
            fullWidth
            decimalScale={formatAmount}
            value={(lens.deductibleCostAmount |> get) || emptyString}
            onChange={event => handleChange('deductibleCostAmount', event)}
            error={!isValid(validation.deductibleCostAmount)}
            helperText={getErrors(validation.deductibleCostAmount)}
            disabled={disabled}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        {
          <Autocomplete
            label={t('InsuranceConventions.Details.DeductibleCosts.Currency')}
            required
            value={{ id: lens.currencyId |> get, code: lens.currencyCode |> get }}
            onChange={currencyChange}
            options={currencies}
            labelKey='code'
            valueKey='id'
            error={!isValid(validation.currencyId)}
            helperText={getErrors(validation.currencyId)}
            disabled={disabled}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton
            type='save'
            color='primary'
            size='tiny'
            variant='text'
            onClick={handleAddOrUpdate}
            tooltip={t('Save')}
            disabled={disabled || !isDirty}
          />
          <IconButton
            type='cancel'
            color='primary'
            size='tiny'
            variant='text'
            onClick={onCancelEdit}
            tooltip={t('Cancel')}
            disabled={disabled}
          />
          {!deductibleCost.isNew && (
            <IconButton
              type='delete'
              color='primary'
              size='tiny'
              variant='text'
              onClick={onDelete}
              tooltip={t('Delete')}
              disabled={disabled}
            />
          )}
        </FlexBox>
      </StyledTableBodyCell>
    </>
  );
};

DeductibleCostEditView.propTypes = {
  deductibleCost: PropTypes.object,
  validation: PropTypes.object,
  currencies: PropTypes.array,
  damageTypes: PropTypes.array,
  disabled: PropTypes.bool,
  formatPercent: PropTypes.number,
  formatAmount: PropTypes.number,
  onAddOrUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  onCancelEdit: PropTypes.func
};

InsuranceConventionDeductibleCostContainer.propTypes = {
  deductibleCost: PropTypes.object,
  validation: PropTypes.object,
  currencies: PropTypes.array,
  damageTypes: PropTypes.array,
  onDelete: PropTypes.func,
  onAddOrUpdate: PropTypes.func,
  onUpdateLocalDeductibleCost: PropTypes.func,
  onCancelAdd: PropTypes.func
};

export default InsuranceConventionDeductibleCostContainer;
